import { InvoiceTask } from '../../../shared/models/invoice.model';

export function formatTimeWorked(minutes: number): string {
  if (!minutes) return '0h';
  const totalHours = Math.floor(minutes / 60);
  const remainingMinutes = Math.floor(minutes % 60);
  let formatted = '';
  if (totalHours > 0) formatted += `${totalHours}h `;
  if (remainingMinutes > 0) formatted += `${remainingMinutes}m `;
  return formatted.trim() || '0h';
}

// Compute minutes for a single start/end pair (expects ISO/UTC strings)
export function computeMinutesFromRange(
  startUtc: string | Date | null | undefined,
  endUtc: string | Date | null | undefined
): number {
  if (!startUtc || !endUtc) return 0;
  const start = new Date(startUtc);
  const end = new Date(endUtc);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  if (end <= start) return 0;

  // Check if times are equal at minute precision to handle timezone/precision issues
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  const startDate = start.getFullYear() * 10000 + start.getMonth() * 100 + start.getDate();
  const endDate = end.getFullYear() * 10000 + end.getMonth() * 100 + end.getDate();

  // If same date and same minute, return 0
  if (startDate === endDate && startMinutes === endMinutes) {
    return 0;
  }

  const diffMs = end.getTime() - start.getTime();
  return Math.round(diffMs / 60000);
}

export function computeWorkedMinutesForTasks(tasks: InvoiceTask[] | null | undefined): number {
  if (!tasks || tasks.length === 0) return 0;

  let minutes = 0;
  for (const task of tasks) {
    if (!task || (task as any).isHospitalised) continue;

    // Skip unchecked tasks (they shouldn't contribute to worked time)
    if (task.isUnchecked) continue;

    // Priority 1: Use totalHours if available and valid
    if (typeof task.totalHours === 'number' && !isNaN(task.totalHours) && task.totalHours > 0) {
      minutes += Math.round(task.totalHours * 60);
    }
    // Priority 2: Calculate from start/end times if both are available
    else if (task.taskStartTime && task.taskEndTime) {
      const start = new Date(task.taskStartTime);
      const end = new Date(task.taskEndTime);

      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end > start) {
        minutes += Math.round((end.getTime() - start.getTime()) / 60000);
      }
    }
  }

  return minutes;
}

export function getStartEndUtcFromTasks(tasks: InvoiceTask[] | null | undefined): {
  startUtc: string | null;
  endUtc: string | null;
} {
  if (!tasks || tasks.length === 0) return { startUtc: null, endUtc: null };

  let startUtc: string | null = null;
  let endUtc: string | null = null;

  for (const t of tasks) {
    if (!t) continue;
    const s = t.taskStartTime || (t as any).date || null;
    const e = t.taskEndTime || (t as any).date || null;

    if (s) {
      if (!startUtc || new Date(s).getTime() < new Date(startUtc).getTime()) {
        startUtc = s;
      }
    }
    if (e) {
      if (!endUtc || new Date(e).getTime() > new Date(endUtc).getTime()) {
        endUtc = e;
      }
    }
  }

  return { startUtc, endUtc };
}
